import React, { useEffect, useRef, useCallback, useMemo } from 'react';

const MirrorDataLattice = ({ theme = 'dark' }) => {
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const nodesRef = useRef(null);

  const COLORS = useMemo(() => ({
    dark: {
      // Un blanc pur pour le coeur, un cyan électrique pour les lignes
      node: 'rgba(255, 255, 255, 0.95)', 
      line: 'rgba(0, 255, 255, 0.5)',     
      glow: 'rgba(0, 255, 255, 0.2)',    
    },
    light: {
      node: 'rgba(160, 120, 50, 0.98)', // Ocre Bronze    
      line: 'rgba(130, 95, 40, 0.8)',    // Bronze Moyen
      glow: 'rgba(210, 170, 80, 0.15)',  
    }
  }), []);

  const projectY = useCallback((y, z, height) => {
    const center = height / 2;
    const depthFactor = 1 - z * 0.35;
    return center + (y - center) * depthFactor;
  }, []);

  const generateNodes = useCallback((width, height) => {
    const marginX = width * 0.09; 
    const paddingX = width * 0.02; 
    const topOffset = height * 0.1;
    const drawHeight = height * 0.9;
    const nodes = [];
    const minDist = 50;

    for (let i = 0; i < 220; i++) {
      const z = Math.random();
      const x = paddingX + (Math.random() * marginX); 
      const y = topOffset + Math.random() * drawHeight;

      if (
        nodes.length < 60 &&
        !nodes.some(n => Math.hypot(n.x - x, n.y - y) < minDist * (0.6 + z))
      ) {
        nodes.push({ x, y, z, size: 0.6 + Math.random() * 1.4 });
      }
    }
    nodesRef.current = nodes;
  }, []);

  const draw = useCallback((currentTheme) => {
    const ctx = ctxRef.current;
    if (!ctx || !nodesRef.current) return;

    const palette = COLORS[currentTheme];
    const width = window.innerWidth;
    const height = window.innerHeight;

    ctx.clearRect(0, 0, width, height);
    const nodes = nodesRef.current;

    const renderSide = (isMirror) => {
      ctx.save();
      if (isMirror) {
        ctx.translate(width, 0);
        ctx.scale(-1, 1);
      }

      // --- RENDU DES CONNEXIONS ---
      for (let i = 0; i < nodes.length; i++) {
        const n1 = nodes[i];
        const y1 = projectY(n1.y, n1.z, height);

        for (let j = i + 1; j < nodes.length; j++) {
          const n2 = nodes[j];
          const dist = Math.hypot(n1.x - n2.x, n1.y - n2.y);
          
          if (dist < 140) {
            const y2 = projectY(n2.y, n2.z, height);
            const depth = (n1.z + n2.z) / 2;

            const gradient = ctx.createLinearGradient(n1.x, y1, n2.x, y2);
            
            // Intensité augmentée au centre pour le côté "neurone actif"
            gradient.addColorStop(0, 'transparent');
            gradient.addColorStop(0.4, palette.line);
            gradient.addColorStop(0.6, palette.line);
            gradient.addColorStop(1, 'transparent');

            ctx.beginPath();
            ctx.lineWidth = 0.4 + depth * 0.7; // Un peu plus épais pour la visibilité
            ctx.globalAlpha = depth * 0.8; 
            ctx.strokeStyle = gradient;
            ctx.moveTo(n1.x, y1);
            ctx.lineTo(n2.x, y2);
            ctx.stroke();
          }
        }
      }

      // --- RENDU DES NOEUDS ---
      nodes.forEach(n => {
        const scale = 0.4 + n.z * 0.8;
        const projectedY = projectY(n.y, n.z, height);
        
        // Glow radial plus large pour l'effet "halo"
        const grad = ctx.createRadialGradient(n.x, projectedY, 0, n.x, projectedY, n.size * 15 * scale);
        grad.addColorStop(0, palette.glow);
        grad.addColorStop(1, 'transparent');

        ctx.globalAlpha = n.z * 0.9;
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(n.x, projectedY, n.size * 15 * scale, 0, Math.PI * 2);
        ctx.fill();

        // Point central
        ctx.globalAlpha = 1;
        ctx.fillStyle = palette.node;
        ctx.beginPath();
        ctx.arc(n.x, projectedY, n.size * scale, 0, Math.PI * 2);
        ctx.fill();
      });

      ctx.restore();
    };

    renderSide(false);
    renderSide(true);
  }, [COLORS, projectY]);

  useEffect(() => {
    const handleResize = () => {
      const canvas = canvasRef.current;
      const dpr = window.devicePixelRatio || 1;
      const width = window.innerWidth;
      const height = window.innerHeight;

      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = width + 'px';
      canvas.style.height = height + 'px';

      const ctx = canvas.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctxRef.current = ctx;

      generateNodes(width, height);
      draw(theme);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [generateNodes, draw, theme]);

  useEffect(() => {
    draw(theme);
  }, [theme, draw]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0, left: 0,
        width: '100vw', height: '100vh',
        zIndex: -1,
        pointerEvents: 'none',
        backgroundColor: 'transparent' // Reste transparent pour laisser voir ton background CSS
      }}
    />
  );
};

export default MirrorDataLattice;